import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Input } from '@/components/Input';
import { useMfaVerify } from '@/lib/auth';
import { ApiError } from '@/lib/api';

const totpSchema = z.object({
  code: z
    .string()
    .regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app'),
});

const recoverySchema = z.object({
  code: z.string().min(6, 'Enter your recovery code'),
});

type FormValues = { code: string };

interface LocationState {
  mfaToken?: string;
}

export function MfaVerify() {
  const location = useLocation();
  const navigate = useNavigate();
  const verify = useMfaVerify();
  const [useRecovery, setUseRecovery] = useState(false);

  const mfaToken = (location.state as LocationState | null)?.mfaToken;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(useRecovery ? recoverySchema : totpSchema),
    defaultValues: { code: '' },
  });

  if (!mfaToken) {
    return <Navigate to="/login" replace />;
  }

  const onSubmit = handleSubmit((values) => {
    verify.mutate(
      { mfaToken, code: values.code.trim() },
      { onSuccess: () => navigate('/dashboard', { replace: true }) },
    );
  });

  const errorMessage =
    verify.error instanceof ApiError
      ? verify.error.message
      : verify.error
        ? 'Could not verify your code. Try again.'
        : null;

  return (
    <div className="w-full max-w-md">
      <h1 className="font-display text-h1 font-bold">Two-step verification</h1>
      <p className="mt-3 text-body text-text-secondary">
        {useRecovery
          ? 'Enter one of the recovery codes you saved when you set up MFA.'
          : 'Open your authenticator app and enter the 6-digit code.'}
      </p>

      <form onSubmit={onSubmit} className="mt-10 flex flex-col gap-gap-element-lg">
        <Field id="code" label={useRecovery ? 'Recovery code' : 'Verification code'} error={errors.code?.message}>
          <Input
            id="code"
            type="text"
            inputMode={useRecovery ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            autoFocus
            invalid={!!errors.code}
            {...register('code')}
          />
        </Field>

        {errorMessage ? (
          <div
            className="rounded-md border border-danger/40 bg-danger/5 px-4 py-3 text-body-sm text-danger"
            role="alert"
          >
            {errorMessage}
          </div>
        ) : null}

        <Button type="submit" size="lg" disabled={verify.isPending}>
          {verify.isPending ? 'Verifying…' : 'Verify'}
        </Button>

        <button
          type="button"
          className="text-left text-body-sm text-accent hover:underline"
          onClick={() => {
            setUseRecovery((v) => !v);
            reset({ code: '' });
          }}
        >
          {useRecovery ? 'Use authenticator code instead' : 'Use a recovery code instead'}
        </button>

        <div className="text-body-sm">
          <Link to="/login" className="text-text-secondary hover:underline">
            Back to sign in
          </Link>
        </div>
      </form>
    </div>
  );
}
